import { throttle } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { updateProjectOrderlinesMailDraft } from '../../../../../features/projectManagement/projectManagementSlice'
import ProjectService from '../../../../../services/ProjectService'
import { MailModel } from '../../../../../shared/models/Mail.model'
import { SaveProjectLineHandlerAssignmentMailDraftModel } from '../../../../../shared/models/service-models/SaveProjectLineHandlerAssignmentMailDraft.model'
import { HandlerAssignmentDraft } from '../../interfaces/pmr-e2e-project-details-orderline-model'

type Props = {
  draft: MailModel
  autoSave: boolean
  e2eProjectLineIds: string[]
  newHandlerInformation: SaveProjectLineHandlerAssignmentMailDraftModel['newHandlerInformation']
}

export const useSaveDraft = ({
  draft,
  e2eProjectLineIds,
  autoSave,
  newHandlerInformation,
}: Props) => {
  const [isManuallySaving, setIsManuallySaving] = useState<boolean>(false)
  const dispatch = useDispatch()
  const [lastSavedDraft, setLastSavedDraft] = useState<HandlerAssignmentDraft>()

  const saveDraft = async (draft: MailModel) => {
    const result = await ProjectService.saveHandlerAssignmentMailDraft({
      draft,
      e2EProjectLineIds: e2eProjectLineIds,
      newHandlerInformation,
    })

    saveDraftToStore(result.data)
    setLastSavedDraft(result.data)

    return { ...result.data, mail: null as any } as HandlerAssignmentDraft
  }

  const saveDraftToStore = (draft: HandlerAssignmentDraft) => {
    dispatch(
      updateProjectOrderlinesMailDraft({
        e2eProjectLineIds: e2eProjectLineIds,
        newDraft: draft,
      })
    )
  }

  const manuallySaveDraft = async () => {
    setIsManuallySaving(true)

    try {
      const savedDraft = await saveDraft(draft)
      setLastSavedDraft(savedDraft)

      return savedDraft
    } finally {
      setIsManuallySaving(false)
    }
  }

  const saveDraftWithThrottle = useMemo(
    () =>
      throttle(
        (draft: MailModel) => {
          autoSave && saveDraft(draft)
        },
        30000,
        { leading: false, trailing: true }
      ),
    [e2eProjectLineIds, autoSave]
  )

  useEffect(() => {
    if (!autoSave || e2eProjectLineIds.length === 0 || !newHandlerInformation || isManuallySaving) {
      return
    }

    saveDraftWithThrottle(draft)
  }, [draft, saveDraftWithThrottle, autoSave])

  // cancel only on unmount, otherwise every keystroke resets the throttle window
  useEffect(() => () => saveDraftWithThrottle.cancel(), [saveDraftWithThrottle])

  return {
    manuallySaveDraft,
    isManuallySaving,
    lastSavedDraft,
    saveDraftToStore,
  }
}
